import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Icons
import { IoSettings } from "react-icons/io5";

const PREFERENCES_KEY = 'mytherra_preferences';

function Preferences() {
  const [notifications, setNotifications] = useState(true);
  const [currency, setCurrency] = useState("USD");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [soundEffects, setSoundEffects] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    const saved = localStorage.getItem(PREFERENCES_KEY);
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
        setNotifications(prefs.notifications ?? true);
        setCurrency(prefs.currency ?? "USD");
        setAutoRefresh(prefs.autoRefresh ?? true);
        setSoundEffects(prefs.soundEffects ?? false);
      } catch (e) {
        console.error('Error loading preferences:', e);
      }
    }
  }, []);

  // Save preferences when they change
  useEffect(() => {
    const prefs = {
      notifications,
      currency,
      autoRefresh,
      soundEffects
    };
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
  }, [notifications, currency, autoRefresh, soundEffects]);

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" mb={1}>
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="48px"
              height="48px"
              borderRadius="12px"
              sx={{ background: "#00CED1" }}
              mr={2}
            >
              <IoSettings size="24px" color="#000000" />
            </VuiBox>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Preferences
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="button" color="text" ml={7}>
            Customize your wallet experience
          </VuiTypography>
        </VuiBox>

        {/* Language */}
        <VuiBox mb={3} p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium">
                Language
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                Interface language
              </VuiTypography>
            </VuiBox>
            <VuiTypography variant="button" color="info" fontWeight="bold">
              English
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* Currency Display */}
        <VuiBox mb={3} p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox mb={1}>
            <VuiTypography variant="button" color="white" fontWeight="medium">
              Currency Display
            </VuiTypography>
            <VuiTypography variant="caption" color="text" display="block">
              Display value in
            </VuiTypography>
          </VuiBox>
          <Select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            sx={{
              width: "100%",
              "& .MuiSelect-select": {
                color: "white",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                padding: "10px",
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 255, 255, 0.1)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <MenuItem value="USD">USD ($)</MenuItem>
            <MenuItem value="EUR">EUR (€)</MenuItem>
            <MenuItem value="GBP">GBP (£)</MenuItem>
            <MenuItem value="BTC">BTC (₿)</MenuItem>
          </Select>
        </VuiBox>

        {/* Notifications */}
        <VuiBox mb={2} p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium">
                Notifications
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                Transaction alerts
              </VuiTypography>
            </VuiBox>
            <Switch checked={notifications} onChange={(e) => setNotifications(e.target.checked)} />
          </VuiBox>
        </VuiBox>

        {/* Auto Refresh */}
        <VuiBox mb={2} p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium">
                Auto Refresh
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                Update data every 30s
              </VuiTypography>
            </VuiBox>
            <Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
          </VuiBox>
        </VuiBox>

        {/* Sound Effects */}
        <VuiBox p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="medium">
                Sound Effects
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                Transaction sounds
              </VuiTypography>
            </VuiBox>
            <Switch checked={soundEffects} onChange={(e) => setSoundEffects(e.target.checked)} />
          </VuiBox>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default Preferences;
